import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBets } from '@/hooks/useBets';
import { useMarkets } from '@/hooks/useMarkets';
import type { Bet } from '@/types/api';
import { computeRecord } from './streak';
import { StreakMeter } from './StreakMeter';
import { BetRow } from './BetRow';
import { EmptyState } from './MarketCard';
import { IconCalendar } from './marketUi';

type Tab = 'all' | Bet['status'];
const TABS: { id: Tab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Open' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
];

/* The prototype's "My bets": streak meter on top, then the history under
   All / Open / Won / Lost tabs. Reads /bets (newest first) and resolves labels
   against the board; see betLabels.ts for why. */
export function BetHistoryPage() {
  const { data, isLoading } = useBets();
  const { data: markets } = useMarkets();
  const [tab, setTab] = useState<Tab>('all');
  const bets = useMemo(() => data?.items ?? [], [data]);
  const record = useMemo(() => computeRecord(bets), [bets]);
  const shown = tab === 'all' ? bets : bets.filter((b) => b.status === tab);

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-teal">Account</span>
        <h1 className="font-display font-extrabold text-3xl tracking-[-0.02em] mt-1">My bets</h1>
        <p className="text-sm text-white/45 mt-1">{record.settled} settled · {record.pending} open</p>
      </div>

      <StreakMeter record={record} />

      <div className="flex gap-1 rounded-xl bg-white/[0.04] p-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${tab === t.id ? 'bg-brand-surface text-brand-teal shadow' : 'text-white/55 hover:text-white'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-white/50 text-sm">Loading…</p>
      ) : shown.length === 0 ? (
        <EmptyState
          icon={IconCalendar}
          title={tab === 'all' ? 'No bets yet' : `No ${TABS.find((t) => t.id === tab)?.label.toLowerCase()} bets`}
          subtitle="Place your first bet from the home or sports tab."
          action={<Link to="/bets" className="mt-1 rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-brand-teal hover:bg-white/10 transition">Browse markets</Link>}
        />
      ) : (
        <div className="flex flex-col gap-2.5">
          {shown.map((b) => <BetRow key={b.id} bet={b} markets={markets} />)}
        </div>
      )}
    </div>
  );
}
